import { cn } from "@newxsystems/shared";
import { DaylightAction } from "../DaylightAction/DaylightAction";
import { DaylightRichText } from "../DaylightRichText/DaylightRichText";
import type { IDaylightCommonContentProps } from "./DaylightCommonContent.props";

export const DaylightCommonContent = ({
  alignment,
  buttons,
  className,
  description,
  descriptionClassName,
  eyebrow,
  heading,
  headingAs = "h2",
  headingClassName,
  layout = "stacked",
}: IDaylightCommonContentProps) => {
  if (!eyebrow && !heading && !description && !buttons?.length) return null;

  const Heading = headingAs;
  const centered = alignment === "center";
  const actions = buttons;
  /** Manual line breaks in editorial headings are authored as newlines. */
  const headingLines = heading?.split("\n").filter((line) => line.length > 0) ?? [];
  const renderHeadingText = () =>
    headingLines.map((line, index) => (
      <span key={index}>
        {index > 0 ? <br /> : null}
        {line}
      </span>
    ));
  const renderActions = (justifyCentered: boolean) =>
    actions?.length ? (
      <div className={cn("flex flex-wrap gap-3", justifyCentered && "justify-center")}>
        {actions.map((button, index) => (
          <DaylightAction action={button} key={`${button.href}-${index}`} />
        ))}
      </div>
    ) : null;

  // Full-width section header: eyebrow on the rail, statement in the wide column.
  if (layout === "rail" && !centered)
    return (
      <div className={cn("grid grid-cols-12 gap-6", className)}>
        {eyebrow ? (
          <div className="col-span-12 md:col-span-4">
            <p className="daylight-label text-primary">{eyebrow}</p>
          </div>
        ) : null}
        <div className={cn("col-span-12 md:col-span-8", !eyebrow && "md:col-start-5")}>
          {heading ? (
            <Heading
              className={cn(
                "text-balance font-daylight-display text-[clamp(2.5rem,2.61vw_+_1.86rem,5rem)] font-bold leading-[0.9] tracking-[-0.04em] text-graphite",
                headingClassName,
              )}
            >
              {renderHeadingText()}
            </Heading>
          ) : null}
          {description ? (
            <DaylightRichText
              className={cn("mt-6 max-w-xl text-lg text-northsea", descriptionClassName)}
              content={description}
            />
          ) : null}
          {actions?.length ? <div className="mt-8">{renderActions(false)}</div> : null}
        </div>
      </div>
    );

  // Default: one column, sized for blocks that put it beside media, lists, or forms.
  return (
    <div
      className={cn(
        "flex max-w-[48rem] flex-col gap-5",
        centered && "mx-auto items-center text-center",
        className,
      )}
    >
      {eyebrow ? <p className="daylight-label text-primary">{eyebrow}</p> : null}
      {heading ? (
        <Heading
          className={cn(
            "text-balance font-daylight-display text-[clamp(2.25rem,5vw,2.875rem)] font-semibold leading-[1.05] tracking-[-0.035em] text-graphite",
            headingClassName,
          )}
        >
          {renderHeadingText()}
        </Heading>
      ) : null}
      {description ? (
        <DaylightRichText
          className={cn("max-w-[65ch] text-northsea", descriptionClassName)}
          content={description}
        />
      ) : null}
      {renderActions(centered)}
    </div>
  );
};

DaylightCommonContent.displayName = "Content::DaylightCommonContent";
